import React, { useState, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  Button,
  toast,
} from '@you/ui'
import { Icon } from '@/components/Icon'
import { Download } from 'lucide-react'

interface ImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  onImport: (file: File) => Promise<void>
  onDownloadTemplate?: () => Promise<void>
  accept?: string
  maxSize?: number // 文件大小限制，单位MB
  description?: React.ReactNode
}

const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onOpenChange,
  title,
  onImport,
  onDownloadTemplate,
  accept = '.xlsx,.xls',
  maxSize = 20, // 默认20MB
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      const file = files[0]
      if (validateFile(file)) {
        setSelectedFile(file)
      }
    }
  }, [])

  const validateFile = (file: File): boolean => {
    // 检查文件类型
    const validTypes = accept.split(',').map((type) => type.trim())
    const isValidType = validTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type)
      }
      return file.type === type
    })

    if (!isValidType) {
      toast.error(`请选择支持的文件格式: ${accept}`)
      return false
    }

    // 检查文件大小
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`文件大小不能超过 ${maxSize}MB`)
      return false
    }

    return true
  }

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && validateFile(file)) {
        setSelectedFile(file)
      }
      // 清空 input 值，允许重复选择同一文件
      e.target.value = ''
    },
    [accept, maxSize]
  )

  const handleUploadAreaClick = useCallback(() => {
    if (!selectedFile) {
      fileInputRef.current?.click()
    }
  }, [selectedFile])

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      toast.error('请先选择文件')
      return
    }

    setIsUploading(true)
    try {
      await onImport(selectedFile)
      toast.success('导入成功')
      onOpenChange(false)
      setSelectedFile(null)
    } catch {
      toast.error('导入失败')
    } finally {
      setIsUploading(false)
    }
  }, [selectedFile, onImport, onOpenChange])

  const handleDownloadTemplate = useCallback(async () => {
    if (!onDownloadTemplate) return

    try {
      await onDownloadTemplate()
    } catch {
      toast.error('模板下载失败')
    }
  }, [onDownloadTemplate])

  const handleClose = useCallback(() => {
    onOpenChange(false)
    setSelectedFile(null)
  }, [onOpenChange])

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null)
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader title={title} />

        <div className="space-y-6 px-4">
          {/* 文件上传区域 */}
          <div
            className={`flex flex-col items-center justify-center border-[1px] border-dashed rounded-lg bg-[#4E57FE05] py-12 transition-all duration-200 ${
              isDragging
                ? 'border-[#8047FF] bg-[#F7F1FF]'
                : selectedFile
                ? 'border-[#8047FF] bg-[#F7F1FF]'
                : 'border-[#4E57FE1A] hover:border-[#8047FF] cursor-pointer'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadAreaClick}
          >
            {!selectedFile ? (
              <>
                <Icon name="uploadCloud" size={108} />
                <div className="text-sm text-gray-400 mt-2 text-center">
                  将文件拖到此处，或
                  <span className="text-[#2C6BFF] underline ml-1">
                    点击上传
                  </span>
                </div>
                {onDownloadTemplate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDownloadTemplate()
                    }}
                    className="text-sm inline-flex items-center mt-2 gap-2 text-[#2C6BFF] hover:text-[#2C6BFF] underline"
                  >
                    <Download className="w-4 h-4" />
                    <span>下载导入模板</span>
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <Icon name="text" size={48} className="text-[#8047FF]" />
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">
                      {selectedFile.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile()
                  }}
                  className="text-sm text-gray-500 hover:text-red-500 mt-2 underline"
                >
                  重新选择文件
                </button>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isUploading}
          >
            取消
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="bg-[#8047FF] hover:bg-[#7A4BE8]"
          >
            {isUploading ? '导入中...' : '开始导入'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImportDialog
